import { auditLogger, isValidUUID } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';

import { getCurrentUserId, verifyOwnership, withSecurity } from './secureServiceWrapper';

export type TimelineEventType =
  | 'client_created'
  | 'client_updated'
  | 'status_changed'
  | 'note_added'
  | 'note_edited'
  | 'file_uploaded'
  | 'research_started'
  | 'research_enhanced'
  | 'research_completed'
  | 'research_failed'
  | 'research_in_progress'
  | 'memo_generated'
  | 'memo_in_progress'
  | 'memo_failed'
  | 'contract_review_started'
  | 'contract_review_completed'
  | 'contract_review_failed';

export interface TimelineEventDiff {
  field: string;
  fieldLabel: string;
  oldValue: string | null;
  newValue: string | null;
}

export interface TimelineEvent {
  id: string;
  type: TimelineEventType;
  title: string;
  subtitle?: string;
  timestamp: string;
  navigateTo?: string;
  status?: string;
  inProgress?: boolean;
  diffs?: TimelineEventDiff[];
}

const truncate = (text: string, max = 100): string => (text.length > max ? `${text.slice(0, max - 1)}…` : text);

interface ResearchRow {
  id: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  result_group_id: string;
  request?: { research_name?: string };
}

interface MemoRow {
  id: string;
  source_result_id: string;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface VaultRow {
  id: string;
  file_name: string;
  friendly_name: string | null;
  document_type: string | null;
  status: string | null;
  created_at: string;
}

interface NoteRow {
  id: string;
  text: string;
  created_at: string;
  last_modified: string | null;
}

interface ContractRow {
  id: string;
  status: string;
  created_at: string;
  completed_at: string | null;
}

interface AuditRow {
  id: string;
  client_id: string;
  changed_at: string;
  changes: Record<string, { old: unknown; new: unknown }>;
}

const FIELD_LABELS: Record<string, string> = {
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  jurisdiction: 'Jurisdiction',
  status: 'Status',
  client_type: 'Client type',
  address_street: 'Street',
  address_city: 'City',
  address_state: 'State / province',
  address_postal: 'Postal code',
  address_country: 'Country',
  practice_area: 'Practice area',
  opposing_party: 'Opposing party',
  referral_source: 'Referral source',
  engagement_date: 'Engagement date',
};

const ADDRESS_FIELDS = new Set([
  'address_street',
  'address_city',
  'address_state',
  'address_postal',
  'address_country',
]);

const formatValue = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') return value;
  return String(value);
};

const titleForChanges = (changes: AuditRow['changes']): string => {
  const fields = Object.keys(changes);
  if (fields.length === 1) {
    const field = fields[0];
    if (field === 'status') return 'Status changed';
    if (field === 'name') return 'Name updated';
    if (ADDRESS_FIELDS.has(field)) return 'Address updated';
    return `${FIELD_LABELS[field] ?? field} updated`;
  }
  if (fields.every((f) => ADDRESS_FIELDS.has(f))) return 'Address updated';
  return 'Client info updated';
};

const subtitleForChanges = (changes: AuditRow['changes']): string | undefined => {
  const fields = Object.keys(changes);
  if (fields.length === 1) {
    const field = fields[0];
    const change = changes[field];
    const oldVal = formatValue(change.old);
    const newVal = formatValue(change.new);
    if (oldVal && newVal) return `${oldVal} → ${newVal}`;
    if (newVal) return `Set to ${newVal}`;
    if (oldVal) return `Cleared (was ${oldVal})`;
    return undefined;
  }
  const labels = fields.slice(0, 4).map((f) => FIELD_LABELS[f] ?? f);
  const remaining = fields.length - labels.length;
  return remaining > 0 ? `${labels.join(', ')} +${remaining}` : labels.join(', ');
};

const buildAuditEvent = (audit: AuditRow): TimelineEvent | null => {
  const fields = Object.keys(audit.changes ?? {});
  if (fields.length === 0) return null;
  const isStatusOnly = fields.length === 1 && fields[0] === 'status';
  const diffs: TimelineEventDiff[] = fields.map((field) => ({
    field,
    fieldLabel: FIELD_LABELS[field] ?? field,
    oldValue: formatValue(audit.changes[field]?.old),
    newValue: formatValue(audit.changes[field]?.new),
  }));
  return {
    id: `audit-${audit.id}`,
    type: isStatusOnly ? 'status_changed' : 'client_updated',
    title: titleForChanges(audit.changes),
    subtitle: subtitleForChanges(audit.changes),
    timestamp: audit.changed_at,
    diffs,
  };
};

const buildResearchEvents = (research: ResearchRow, isFirstInGroup: boolean, researchName: string): TimelineEvent[] => {
  const status = (research.status || '').toUpperCase();
  const isRunning = status === 'IN_PROGRESS' || status === 'ENHANCING';

  if (isRunning) {
    const runningTitle = status === 'ENHANCING' || !isFirstInGroup ? 'Research updating' : 'Research running';
    return [
      {
        id: `research-progress-${research.id}`,
        type: 'research_in_progress',
        title: runningTitle,
        subtitle: researchName,
        timestamp: research.created_at,
        navigateTo: `/research/${research.id}`,
        inProgress: true,
      },
    ];
  }

  const events: TimelineEvent[] = [];
  const startedType: TimelineEventType = isFirstInGroup ? 'research_started' : 'research_enhanced';
  const startedTitle = isFirstInGroup ? 'Research started' : 'Research enhanced';

  events.push({
    id: `research-start-${research.id}`,
    type: startedType,
    title: startedTitle,
    subtitle: researchName,
    timestamp: research.created_at,
    navigateTo: `/research/${research.id}`,
    status,
  });

  if (research.completed_at && status === 'COMPLETED') {
    events.push({
      id: `research-done-${research.id}`,
      type: 'research_completed',
      title: 'Research completed',
      subtitle: researchName,
      timestamp: research.completed_at,
      navigateTo: `/research/${research.id}`,
    });
  } else if (status === 'FAILED') {
    const failedAt = research.completed_at || research.created_at;
    events.push({
      id: `research-failed-${research.id}`,
      type: 'research_failed',
      title: 'Research failed',
      subtitle: researchName,
      timestamp: failedAt,
      navigateTo: `/research/${research.id}`,
    });
  }

  return events;
};

const buildMemoEvents = (memo: MemoRow, researchById: Map<string, ResearchRow>): TimelineEvent[] => {
  const research = researchById.get(memo.source_result_id);
  const researchName = research?.request?.research_name || 'Research';
  const status = (memo.status || '').toUpperCase();

  if (status === 'COMPLETED' && memo.completed_at) {
    return [
      {
        id: `memo-done-${memo.id}`,
        type: 'memo_generated',
        title: 'Memo generated',
        subtitle: researchName,
        timestamp: memo.completed_at,
        navigateTo: research ? `/research/${research.id}` : undefined,
      },
    ];
  }
  if (status === 'IN_PROGRESS') {
    return [
      {
        id: `memo-progress-${memo.id}`,
        type: 'memo_in_progress',
        title: 'Memo generating',
        subtitle: researchName,
        timestamp: memo.created_at,
        navigateTo: research ? `/research/${research.id}` : undefined,
        inProgress: true,
      },
    ];
  }
  if (status === 'FAILED') {
    return [
      {
        id: `memo-failed-${memo.id}`,
        type: 'memo_failed',
        title: 'Memo failed',
        subtitle: researchName,
        timestamp: memo.completed_at || memo.created_at,
        navigateTo: research ? `/research/${research.id}` : undefined,
      },
    ];
  }
  return [];
};

const buildContractEvents = (review: ContractRow): TimelineEvent[] => {
  const events: TimelineEvent[] = [];
  const status = (review.status || '').toUpperCase();

  events.push({
    id: `contract-start-${review.id}`,
    type: 'contract_review_started',
    title: 'Contract review started',
    timestamp: review.created_at,
    inProgress: status === 'IN_PROGRESS',
  });

  if (status === 'COMPLETED' && review.completed_at) {
    events.push({
      id: `contract-done-${review.id}`,
      type: 'contract_review_completed',
      title: 'Contract review completed',
      timestamp: review.completed_at,
    });
  } else if (status === 'FAILED') {
    events.push({
      id: `contract-failed-${review.id}`,
      type: 'contract_review_failed',
      title: 'Contract review failed',
      timestamp: review.completed_at || review.created_at,
    });
  }

  return events;
};

export const clientTimelineService = {
  getClientTimeline: async (clientId: string, currentClientName?: string): Promise<TimelineEvent[]> => {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientTimeline' });
          return [];
        }

        await verifyOwnership('clients', clientId);

        const supabase = await getSupabase();
        const events: TimelineEvent[] = [];

        const [clientRow, notes, files, researchData, auditData] = await Promise.all([
          supabase.from('clients').select('id, name, created_at').eq('id', clientId).maybeSingle(),
          supabase.from('client_notes').select('id, text, created_at, last_modified').eq('client_id', clientId),
          supabase
            .from('client_vault_documents')
            .select('id, file_name, friendly_name, document_type, status, created_at')
            .eq('client_id', clientId),
          supabase
            .from('legal_search_results')
            .select('id, status, created_at, completed_at, result_group_id, request')
            .eq('client_id', clientId)
            .order('created_at', { ascending: true }),
          supabase
            .from('client_audit_log')
            .select('id, client_id, changed_at, changes')
            .eq('client_id', clientId)
            .order('changed_at', { ascending: false }),
        ]);

        if (clientRow.data?.created_at) {
          const name = currentClientName || clientRow.data.name || 'Client';
          events.push({
            id: `client-${clientRow.data.id}`,
            type: 'client_created',
            title: `${name} added`,
            subtitle: 'Client created',
            timestamp: clientRow.data.created_at,
          });
        }

        for (const audit of (auditData.data ?? []) as AuditRow[]) {
          const event = buildAuditEvent(audit);
          if (event) events.push(event);
        }

        for (const note of (notes.data ?? []) as NoteRow[]) {
          const wasEdited = !!note.last_modified && note.last_modified !== note.created_at;
          events.push({
            id: `note-${note.id}`,
            type: wasEdited ? 'note_edited' : 'note_added',
            title: wasEdited ? 'Note edited' : 'Note added',
            subtitle: truncate(note.text || ''),
            timestamp: wasEdited ? (note.last_modified as string) : note.created_at,
          });
        }

        const fileIds: string[] = [];
        for (const file of (files.data ?? []) as VaultRow[]) {
          fileIds.push(file.id);
          const displayName = file.friendly_name || file.file_name;
          const fileStatus = (file.status || '').toUpperCase();
          const subtitle = file.document_type ? `${displayName} · ${file.document_type}` : displayName;
          events.push({
            id: `file-${file.id}`,
            type: 'file_uploaded',
            title: 'File uploaded',
            subtitle,
            timestamp: file.created_at,
            inProgress: fileStatus === 'IN_PROGRESS' || fileStatus === 'PENDING',
          });
        }

        const researchRows = (researchData.data ?? []) as ResearchRow[];
        const firstByGroup = new Map<string, string>();
        for (const research of researchRows) {
          if (!firstByGroup.has(research.result_group_id)) {
            firstByGroup.set(research.result_group_id, research.id);
          }
        }
        const researchById = new Map<string, ResearchRow>();
        for (const research of researchRows) {
          researchById.set(research.id, research);
          const researchName = research.request?.research_name || 'Untitled research';
          const isFirst = firstByGroup.get(research.result_group_id) === research.id;
          events.push(...buildResearchEvents(research, isFirst, researchName));
        }

        const researchIds = researchRows.map((r) => r.id);
        if (researchIds.length > 0) {
          const memoRes = await supabase
            .from('research_memo_results')
            .select('id, source_result_id, status, created_at, completed_at')
            .in('source_result_id', researchIds);
          for (const memo of (memoRes.data ?? []) as MemoRow[]) {
            events.push(...buildMemoEvents(memo, researchById));
          }
        }

        if (fileIds.length > 0) {
          const contractRes = await supabase
            .from('contract_review_results')
            .select('id, status, created_at, completed_at, client_id, request')
            .or(`client_id.eq.${clientId}`);
          for (const review of (contractRes.data ?? []) as ContractRow[]) {
            events.push(...buildContractEvents(review));
          }
        }

        events.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
        return events;
      },
      { requireAuth: true, rateLimitKey: 'client_timeline_get' }
    );
  },

  subscribeToTimelineUpdates: async (clientId: string, callback: () => void) => {
    if (!isValidUUID(clientId)) {
      auditLogger.log('VALIDATION_FAILURE', { reason: 'invalid_client_id', service: 'clientTimeline_subscribe' });
      return null;
    }

    const currentUserId = await getCurrentUserId();
    if (!currentUserId) {
      auditLogger.log('AUTH_FAILURE', { reason: 'no_user', service: 'clientTimeline_subscribe' });
      return null;
    }

    const isOwner = await verifyOwnership('clients', clientId);
    if (!isOwner) {
      auditLogger.log('OWNERSHIP_VIOLATION', { reason: 'subscribe_not_owner', service: 'clientTimeline_subscribe' });
      return null;
    }

    const supabase = await getSupabase();
    const channel = supabase.channel(`client_timeline_${clientId}_${crypto.randomUUID()}`);
    const clientFilter = `client_id=eq.${clientId}`;
    const tablesByClient: { table: string; filter?: string }[] = [
      { table: 'legal_search_results', filter: clientFilter },
      { table: 'client_notes', filter: clientFilter },
      { table: 'client_vault_documents', filter: clientFilter },
      { table: 'client_audit_log', filter: clientFilter },
      { table: 'contract_review_results', filter: clientFilter },
      { table: 'research_memo_results' },
    ];

    for (const { table, filter } of tablesByClient) {
      channel.on(
        'postgres_changes',
        filter ? { event: '*', schema: 'public', table, filter } : { event: '*', schema: 'public', table },
        () => callback()
      );
    }

    await channel.subscribe();
    return channel;
  },
};
